import { useEffect, useState } from 'react';
import { getPromoCodeDetails } from '@/lib/api/subscriptions';
import { PromoCodeValidation } from '../types/subscription.types';
import { QUERY_KEYS, SENTRY_FEATURES, stanNotify } from '@/lib';
import { useStanQuery } from '@/queries';
import { SUBSCRIPTION_STORAGE_KEYS } from '../constants';

export const usePromoCode = () => {
    const [promoCode, setPromoCode] = useState('');
    const [appliedCode, setAppliedCode] = useState('');
    const [validation, setValidation] = useState<PromoCodeValidation>({
        isValid: false,
    });

    const {
        data: details,
        isLoading,
        isError,
    } = useStanQuery({
        queryKey: [QUERY_KEYS.PROMO_CODE_DETAILS, appliedCode],
        queryFn: () => getPromoCodeDetails(appliedCode),
        enabled: !!appliedCode,
        retry: false,
        staleTime: Infinity,
        meta: {
            error: {
                feature: SENTRY_FEATURES.AUTHENTICATION,
                sentryMessage: '[AUTHENTICATION] failed-to-validate-promo-code',
                title: 'Promo Code Error',
                message: 'Failed to validate promo code. Please try again.',
            },
        },
    });

    useEffect(() => {
        if (!appliedCode) {
            setValidation({ isValid: false });
            return;
        }

        if (isLoading) return;

        if (isError) {
            setValidation({
                isValid: false,
                errorMessage: 'This promo code is invalid or expired',
            });
            stanNotify({
                type: 'error',
                title: 'Invalid discount code',
                text: 'Unable to apply discount code.',
            });
            localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
            return;
        }

        if (details?.promo_code && details.promo_code.active) {
            const coupon = details.promo_code.coupon;
            const isFree = coupon.percent_off === 100;

            setValidation({
                isValid: true,
                promoId: details.promo_code.id,
                discountPercent: coupon.percent_off,
                isFreeSubscription: isFree,
            });

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Discount code successfully applied.',
            });

            if (isFree) {
                localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'false');
            } else {
                localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
            }

            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID, details.promo_code.id);
        } else if (details) {
            // Details loaded but invalid/inactive
            setValidation({
                isValid: false,
                errorMessage: 'This promo code is invalid or expired',
            });
            stanNotify({
                type: 'error',
                title: 'Invalid discount code',
                text: 'Unable to apply discount code.',
            });
            localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
        }
    }, [details, isLoading, isError, appliedCode]);

    const validatePromoCode = (code: string) => {
        if (!code.trim()) {
            setValidation({ isValid: false });
            return;
        }
        setAppliedCode(code.trim());
    };

    const clearPromoCode = () => {
        setPromoCode('');
        setAppliedCode('');
        setValidation({ isValid: false });
        localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, 'true');
    };

    return {
        promoCode,
        setPromoCode,
        isValidating: isLoading,
        validation,
        validatePromoCode,
        clearPromoCode,
    };
};
